// The spreadsheet table's data rules (Leon, 2026-10-23): filter by values
// (Sheets' way — an unticked value hides its rows), filter by condition, sort,
// sums and tab-separated copy. Pure, so each rule is tested rather than
// eyeballed; the grid only draws what these return. Cell-range selection is
// sheet-selection.ts, grouping sheet-group.ts, views and chips sheet-view.ts.

export type SheetKind = "text" | "number";

export interface SheetColumn {
  key: string;
  label: string;
}

export type SortDir = "asc" | "desc";

export interface SheetSort {
  key: string;
  dir: SortDir;
}

export type SheetCondition =
  | { op: "contains"; value: string }
  | { op: "equals"; value: string }
  | { op: "gt"; value: number }
  | { op: "lt"; value: number }
  | { op: "between"; from: number; to: number }
  | { op: "empty" }
  | { op: "not_empty" }
  /** A date range. A preset is resolved against today each time, so "This month" stays current. */
  | { op: "date"; preset: DatePreset | null; from: string; to: string };

export type DatePreset = "today" | "week" | "month";

const MANILA_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Manila",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The Philippine calendar day of a date or timestamp, as YYYY-MM-DD; "" when it isn't one. */
export function manilaDay(value: unknown): string {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : MANILA_DAY.format(value);
  const text = cellText(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const time = new Date(text);
  return text && !Number.isNaN(time.getTime()) ? MANILA_DAY.format(time) : "";
}

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/** The days a preset covers, Monday-first weeks, for a given Philippine `today`. */
export function presetRange(preset: DatePreset, today: string): { from: string; to: string } {
  const day = new Date(`${today}T00:00:00Z`);
  if (preset === "today") return { from: today, to: today };
  if (preset === "week") {
    const monday = new Date(day);
    monday.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return { from: isoDay(monday), to: isoDay(sunday) };
  }
  const first = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1));
  const last = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0));
  return { from: isoDay(first), to: isoDay(last) };
}

export type SheetRow = Record<string, unknown>;

export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === "";
}

/** The stored text a cell is matched and filtered by. */
export function cellText(value: unknown): string {
  return isBlank(value) ? "" : String(value);
}

export function asNumber(value: unknown): number | null {
  if (isBlank(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** A column is numeric when every filled cell is a number (money, km, counts). */
export function inferKind(rows: readonly SheetRow[], key: string): SheetKind {
  let filled = 0;
  for (const row of rows) {
    if (isBlank(row[key])) continue;
    if (asNumber(row[key]) === null) return "text";
    filled += 1;
  }
  return filled > 0 ? "number" : "text";
}

export function matchesCondition(
  value: unknown,
  condition: SheetCondition,
  today: string = manilaDay(new Date()),
): boolean {
  switch (condition.op) {
    case "date": {
      const day = manilaDay(value);
      if (!day) return false;
      const range = condition.preset ? presetRange(condition.preset, today) : condition;
      return (!range.from || day >= range.from) && (!range.to || day <= range.to);
    }
    case "empty":
      return isBlank(value);
    case "not_empty":
      return !isBlank(value);
    case "contains":
      return cellText(value).toLowerCase().includes(condition.value.trim().toLowerCase());
    case "equals": {
      const number = asNumber(value);
      const wanted = asNumber(condition.value);
      if (number !== null && wanted !== null) return number === wanted;
      return cellText(value).toLowerCase() === condition.value.trim().toLowerCase();
    }
    default: {
      const number = asNumber(value);
      if (number === null) return false;
      if (condition.op === "gt") return number > condition.value;
      if (condition.op === "lt") return number < condition.value;
      return number >= condition.from && number <= condition.to;
    }
  }
}

/**
 * Keeps the rows passing every column's filter: no hidden value, and its
 * condition met. `hidden` lists stored values per column (blank is "").
 */
export function applyFilters<T extends SheetRow>(
  rows: readonly T[],
  hidden: Readonly<Record<string, readonly string[]>>,
  conditions: Readonly<Record<string, SheetCondition>>,
  today: string = manilaDay(new Date()),
): T[] {
  const hiddenSets = Object.entries(hidden)
    .filter(([, values]) => values.length > 0)
    .map(([key, values]) => [key, new Set(values)] as const);
  const active = Object.entries(conditions);
  if (hiddenSets.length === 0 && active.length === 0) return [...rows];
  return rows.filter(
    (row) =>
      hiddenSets.every(([key, values]) => !values.has(cellText(row[key]))) &&
      active.every(([key, condition]) => matchesCondition(row[key], condition, today)),
  );
}

/** Blanks always sink to the bottom, whichever way the column is sorted. */
export function sortRows<T extends SheetRow>(
  rows: readonly T[],
  sort: SheetSort | null,
  kind: SheetKind,
): T[] {
  if (!sort) return [...rows];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[sort.key];
    const y = b[sort.key];
    if (isBlank(x) || isBlank(y)) return isBlank(x) === isBlank(y) ? 0 : isBlank(x) ? 1 : -1;
    if (kind === "number") return sign * ((asNumber(x) ?? 0) - (asNumber(y) ?? 0));
    return sign * cellText(x).localeCompare(cellText(y), undefined, { numeric: true });
  });
}

export interface ValueCount {
  value: string;
  label: string;
  count: number;
}

/** The filter menu's value list: every distinct stored value with how many rows hold it. */
export function valueCounts(
  rows: readonly SheetRow[],
  key: string,
  labelOf: (value: string) => string = (value) => value,
): ValueCount[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = cellText(row[key]);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count, label: value === "" ? "(Blank)" : labelOf(value) }))
    .sort((a, b) =>
      a.value === ""
        ? 1
        : b.value === ""
          ? -1
          : a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
}

/**
 * A column's starting width: wide enough for its header and its longest value
 * among the first rows, within bounds. An estimate by character count, so no
 * text has to be measured; the operator drags a column wider when it guesses short.
 */
export function autoWidth(label: string, texts: readonly string[], min = 110, max = 320): number {
  const longest = texts.reduce((most, text) => Math.max(most, text.length), 0);
  const header = label.length * 7.6 + 52; // uppercase, letter-spaced, plus the funnel
  const cell = longest * 7.8 + 28;
  return Math.round(Math.min(max, Math.max(min, header, cell)));
}

/** Sum a numeric column; cells that aren't numbers are skipped. Rounded to centavos. */
export function sumOf(rows: readonly SheetRow[], key: string): number {
  let total = 0;
  for (const row of rows) total += asNumber(row[key]) ?? 0;
  return Math.round(total * 100) / 100;
}

// Sheets runs a pasted cell that starts with one of these as a formula.
const FORMULA_START = /^[\s]*[=+\-@]/;

/** One cell for a tab-separated paste: no tabs or breaks inside, formulas defused. */
export function tsvCell(text: string): string {
  const flat = text.replace(/[\t\r\n]+/g, " ");
  // A negative amount is a number, not a formula — keep it pasteable as one.
  if (FORMULA_START.test(flat) && asNumber(flat) === null) return `'${flat}`;
  return flat;
}
